import React, { useCallback, useContext } from 'react';
import { connect } from 'react-redux';

import { ReviewToolContext } from '../ReviewTool/Context/reviewToolContext';
// Interfaces
import {
  assetMimeTypes,
  assetProcessingStates
} from '@cube3/common/model/types';
/* Import components */
import ImagePreview from '@cube3/ui/src/previews/ImagePreview/ImagePreview';
import VideoPreview from '@cube3/ui/src/previews/VideoPreview/MediaPlayer';
import { AudioPreview } from '@cube3/ui/src/previews/AudioPreview';
import { DocumentPreview } from '@cube3/ui/src/previews/DocumentPreview';
import { FallbackPreview } from '@cube3/ui/src/previews/FallbackPreview';
import PreviewHoc from './PreviewHoc';
import PreviewVideoHoc from './PreviewVideoHoc';
import { CubeReduxState } from '@cube3/state/src/redux/ducks/CubeReduxState';
import { Modal } from '@cube3/state/src/redux/ducks/modals/state';
import { AnnotationLayer } from '../Annotation/AnnotationLayer';
// Import Hooks
import { matchPath, useLocation } from 'react-router-dom';
import { urlStructureReview } from '../../routing/routingPaths';
import { Spectogram2 } from './DetailView/Preview/Spectogram2';
import { usePollReviewComments } from '../ReviewTool/Hooks/usePollReviewComments';
import { useAssetViewed } from './contexts/AssetViewedProvider';
import { VideoDragSeeek } from './DetailView/Preview/VideoDragSeek';
import { SocialMediaMockupLayer } from './DetailView/Preview/SocialMediaMockups/SocialMediaMockupLayer';
import { Html5BannerPreview } from './Html5BannerPreview';

/* Wrap components with previewHOC */
const ConnectedImagePreview = PreviewHoc(ImagePreview);
const ConnectedDocumentPreview = PreviewHoc(DocumentPreview);
const ConnectedVideoPreview = PreviewVideoHoc(VideoPreview);
const ConnectedAudioPreview = PreviewHoc(AudioPreview);
const ConnectedFallbackPreview = PreviewHoc(FallbackPreview);
const ConnectedBannerPreview = PreviewHoc(Html5BannerPreview);

const mapStateToProps = (state: CubeReduxState) => {
  return {
    fullscreen: state.ui.fullscreen,
    fullscreenRequested: state.ui.fullscreenRequested,
    modalStack: state.modals.modalStack
  };
};

interface PreviewProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  asset: any;
  gotoNextSibling?: Function;
  gotoPrevSibling?: Function;
  siblings?: boolean;
  loading?: boolean;
  // props recieved from state
  fullscreen: boolean;
  fullscreenRequested: boolean;
  modalStack: Modal[];
  disableSnapshot?: boolean;
  //Navigation arrows for preview view
  showNavArrows?: boolean;
  setShowNavArrows?: React.Dispatch<React.SetStateAction<boolean>>;
}

const audioLayers = [Spectogram2];
const videoPreviewLayersBase = [VideoDragSeeek];
const videoPreviewLayersReview = [AnnotationLayer, ...videoPreviewLayersBase];
const videoPreviewLayersReviewWithMockupTemplates = [SocialMediaMockupLayer, ...videoPreviewLayersBase];
const imagePreviewLayersReview = [AnnotationLayer];

function Preview(props: PreviewProps) {
  const {
    asset = { type: '' },
    gotoNextSibling = () => null,
    gotoPrevSibling = () => null,
    siblings = false,
    loading,
    modalStack,
    disableSnapshot,
    showNavArrows,
    setShowNavArrows
  } = props;

  const { pathname } = useLocation();
  const onReviewRoute = matchPath(pathname, urlStructureReview);


  const pages =
    asset &&
    asset.media &&
    asset.media
      .filter((m) => m.type === 'poster')
      .sort((pA, pB) => {
        return parseInt(pA.page) - parseInt(pB.page);
      });
  const source = asset?.media?.find((m) => m.type === 'source');
  const preview = asset?.media?.find((m) => m.type === 'preview');
  const poster = pages && pages[0];
  const thumbnail = asset?.media?.find((m) => m.type === 'thumbnail');
  const strip = asset?.media?.find((m) => m.type === 'strip');
  const src = preview || poster;

  const dimensions = poster
    ? { width: poster.width, height: poster.height }
    : undefined;

  // we will check if there are any other modals active and prop drill that to videopreviewcontrols
  // so we can prevent keyboard input for the video player.
  const otherModalsActive: boolean =
    modalStack && modalStack.length > 0 ? true : false;

  // review view
  const { unresolvedComments: comments } = usePollReviewComments(
    onReviewRoute ? asset.id : undefined
  );


  const {
    drawingBarToggled,
    playerRef,
    toggleDrawingBar,
    activeComment,
    setActiveComment,
    activeTemplate
  } = useContext(ReviewToolContext) || {};

  // get asset view context value
  const viewedContext = useAssetViewed();

  const showAnnotation = onReviewRoute && !activeTemplate

  const videoPreviewLayers = onReviewRoute
    ? activeTemplate ? videoPreviewLayersReviewWithMockupTemplates : videoPreviewLayersReview
    : videoPreviewLayersBase;

  const renderSocialMediaMockupLayer = useCallback(() => {
    if (!src || !activeTemplate) return null;
    return <SocialMediaMockupLayer template={activeTemplate as any} src={src.url} type='image' />
  }, [src, activeTemplate])

  switch (
  !asset.media || asset.processing_status !== assetProcessingStates.COMPLETE
    ? 'not_ready'
    : asset.mime_type
  ) {
    case assetMimeTypes.VIDEO:
      return (
        <ConnectedVideoPreview
          key={`preview-asset-${asset.id}`}
          {...asset}
          src={src && src.url}
          poster={poster && poster.url}
          status={asset.state}
          thumbnail={thumbnail && thumbnail.url}
          dimensions={{ width: source?.width, height: source?.height }}
          gotoNextSibling={gotoNextSibling}
          gotoPrevSibling={gotoPrevSibling}
          siblings={siblings}
          otherModalsActive={otherModalsActive}
          stripSrc={strip?.url}
          disableSnapshot={disableSnapshot}
          // review view
          layers={videoPreviewLayers}
          toggleDrawingBar={showAnnotation ? toggleDrawingBar : undefined}
          drawingBarToggled={showAnnotation && drawingBarToggled}
          comments={comments}
          setActiveComment={setActiveComment}
          activeComment={activeComment}
          playerRef={playerRef}
          template={activeTemplate}
          {...viewedContext}
          showNavArrows={showNavArrows}
          setShowNavArrows={setShowNavArrows}
        />
      );

    case assetMimeTypes.IMAGE:
      return (
        <ConnectedImagePreview
          key={`preview-asset-${asset.id}`}
          {...asset}
          src={src && src.url}
          status={asset.state}
          thumbnail={thumbnail && thumbnail.url}
          gotoNextSibling={gotoNextSibling}
          gotoPrevSibling={gotoPrevSibling}
          siblings={siblings}
          dimensions={{ width: source.width, height: source.height }}
          layers={onReviewRoute && imagePreviewLayersReview}
          renderSocialMediaMockupLayer={onReviewRoute && renderSocialMediaMockupLayer}
          drawingBarToggled={showAnnotation && drawingBarToggled}
          toggleDrawingBar={toggleDrawingBar}
        />
      );

    case assetMimeTypes.DOCUMENT_PDF:
      // in some cases processing (and bad database) does not return pages
      // in the source, so only render preview when pages are available.
      if (pages && pages.length > 0) {
        return (
          <ConnectedDocumentPreview
            key={`preview-asset-${asset.id}`}
            {...asset}
            pages={pages}
            status={asset.state}
            thumbnail={thumbnail && thumbnail.url}
            dimensions={dimensions}
            gotoNextSibling={gotoNextSibling}
            gotoPrevSibling={gotoPrevSibling}
            siblings={siblings}
          />
        );
      }
      break;

    case assetMimeTypes.AUDIO:
      return (
        <ConnectedAudioPreview
          key={`preview-asset-${asset.id}`}
          {...asset}
          src={src && src.url}
          status={asset.state}
          thumbnail={thumbnail && thumbnail.url}
          gotoNextSibling={gotoNextSibling}
          gotoPrevSibling={gotoPrevSibling}
          siblings={siblings}
          otherModalsActive={otherModalsActive}
          layers={audioLayers}
          {...viewedContext}
          showNavArrows={showNavArrows}
          setShowNavArrows={setShowNavArrows}
        />
      );
    case assetMimeTypes.BANNER:
      return <ConnectedBannerPreview asset={asset} />;
  }

  // because we hit some cases in the switch statement but choose to do conditional rendering within the case,
  // we will make sure make sure that we always render the fallback in case switch statement returns nothing.
  return (
    <ConnectedFallbackPreview
      key={`preview-asset-${asset.id}`}
      {...asset}
      status={asset.state}
      gotoNextSibling={gotoNextSibling}
      gotoPrevSibling={gotoPrevSibling}
      siblings={siblings}
      loading={loading}
    />
  );
}

export default connect(mapStateToProps, {})(Preview);
